/**
 * tagRegistry — relay-66 / EFISSUE-820 Part 1.
 *
 * Pure helpers for the composer's `#` tag picker. Mirrors the shape of
 * `mentionParser.ts` so ERComposerV1 can wire `#` and `@` triggers in
 * parallel.
 *
 * Triggering rules (mirrors @-mention):
 *   - The `#` must be at the very start of the content OR preceded by
 *     whitespace. `foo#bar` typed in the body does NOT trigger.
 *   - The text between the `#` and the cursor must contain only word
 *     chars (letters/digits/_/-). Stops at any whitespace, second `#`,
 *     or end of allowed range.
 *   - Cursor must be AT or AFTER the `#`.
 *
 * Tag vocabulary (`STANDARD_TAGS`) covers Charter v12 conventions:
 * lifecycle tags, ACP events, priority, status, common labels. The
 * picker also surfaces `loadRecentTags(workspaceId)` from localStorage
 * so frequently-used tags (including ones not in the standard list)
 * are surfaced first.
 */

export interface TagMatch {
  /** Index of the `#` character in the input. */
  start: number;
  /** Substring between `#` and cursor; `""` immediately after typing `#`. */
  query: string;
}

const TAG_WORD_RE = /^[A-Za-z0-9_:-]*$/;

export function parseTagAt(text: string, cursorPos: number): TagMatch | null {
  if (cursorPos < 0 || cursorPos > text.length) return null;
  for (let i = cursorPos - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === "#") {
      const before = i === 0 ? "" : text[i - 1];
      if (before !== "" && !/\s/.test(before)) return null;
      const query = text.slice(i + 1, cursorPos);
      if (!TAG_WORD_RE.test(query)) return null;
      return { start: i, query };
    }
    if (/\s/.test(ch)) return null;
  }
  return null;
}

/** Personal tags — surfaced first in picker for quick access. */
export const PERSONAL_TAGS: readonly string[] = Object.freeze([
  "help",
]);

/** Charter v12 tag vocabulary. Order does not matter — sorted at filter time. */
export const STANDARD_TAGS: readonly string[] = Object.freeze([
  // Workflow rituals
  "pre-flight",
  "pre-push-checklist",
  "pre-push-cleared",
  "pre-push-blocked",
  "build-complete",
  "deployed",
  "blocker",
  "nudge",
  "approved",
  "directive",
  "relay-system",
  "relay-help",
  "onboarding",
  "charter-proposal",
  // Chat
  "chat",
  // Priority
  "p0",
  "p1",
  "p2",
  "p3",
  // Issue tracking
  "issue",
  "status:backlog",
  "status:active",
  "status:done",
  "status:cancelled",
  // SDLC lifecycle
  "idea",
  "spec",
  "staging",
  "test-result",
  "defect",
  "released",
  "resolved",
  // ACP events
  "question",
  "finding",
  "retiring",
  // Other
  "reference",
]);

const RECENT_TAGS_LIMIT = 20;

function recentKey(workspaceId: string): string {
  return `relay_tag_history_${workspaceId}`;
}

export function loadRecentTags(workspaceId: string): string[] {
  try {
    const raw = localStorage.getItem(recentKey(workspaceId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((t): t is string => typeof t === "string").slice(0, RECENT_TAGS_LIMIT);
  } catch {
    return [];
  }
}

export function pushRecentTag(workspaceId: string, tag: string): void {
  if (!tag) return;
  try {
    const current = loadRecentTags(workspaceId);
    const next = [tag, ...current.filter((t) => t !== tag)].slice(0, RECENT_TAGS_LIMIT);
    localStorage.setItem(recentKey(workspaceId), JSON.stringify(next));
  } catch {
    /* localStorage unavailable / quota — silently no-op */
  }
}

/**
 * Filter ranking: personal tags matching the query come first (for quick
 * access to notes/reminders/help), then recently-used tags (in recency order),
 * then standard-vocabulary tags (alphabetical). Case-insensitive prefix match.
 * Empty query returns the same combined list.
 */
export function fuzzyFilterTags(
  query: string,
  standards: readonly string[] = STANDARD_TAGS,
  recents: readonly string[] = [],
  personal: readonly string[] = PERSONAL_TAGS,
): string[] {
  const q = query.toLowerCase();
  const personalMatches = personal.filter((t) => t.toLowerCase().startsWith(q));
  const personalSet = new Set(personalMatches);
  const recentMatches = recents.filter(
    (t) => t.toLowerCase().startsWith(q) && !personalSet.has(t)
  );
  const recentSet = new Set([...personalMatches, ...recentMatches]);
  const standardMatches = [...standards]
    .filter((t) => t.toLowerCase().startsWith(q) && !recentSet.has(t))
    .sort((a, b) => a.localeCompare(b));
  return [...personalMatches, ...recentMatches, ...standardMatches];
}
